<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useEventSignatureMedia, MAX_SIGNATURE_IMAGES } from '@/composables/useEventSignatureMedia'

const { t } = useI18n()
const route = useRoute()

const eventSlug = route.params['eventSlug'] as string

const {
  images,
  loading,
  uploadProgress,
  fetchImages,
  uploadImage,
  deleteImage,
  stopAllPolling,
} = useEventSignatureMedia(eventSlug)

const inputRef = ref<HTMLInputElement | null>(null)
const isDragging = ref(false)
const isUploading = ref(false)
const clientError = ref<string | null>(null)
const fieldError = ref<string | null>(null)

const canUploadMore = computed(() => images.value.length < MAX_SIGNATURE_IMAGES)

onMounted(() => void fetchImages())
onUnmounted(() => stopAllPolling())

function openFilePicker(): void {
  inputRef.value?.click()
}

function onDragOver(): void {
  isDragging.value = true
}

function onDragLeave(): void {
  isDragging.value = false
}

async function onDrop(event: DragEvent): Promise<void> {
  isDragging.value = false
  const file = event.dataTransfer?.files[0]
  if (file) {
    await handleFile(file)
  }
}

async function onFileChange(event: Event): Promise<void> {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  if (file) {
    await handleFile(file)
  }
  input.value = ''
}

async function handleFile(file: File): Promise<void> {
  clientError.value = null
  fieldError.value = null

  if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
    clientError.value = t('events.media.signature.error_mime')
    return
  }

  if (file.size > 10 * 1024 * 1024) {
    clientError.value = t('events.media.signature.error_size')
    return
  }

  isUploading.value = true
  try {
    await uploadImage(file)
  } catch (e: unknown) {
    const err = e as { response?: { data?: { errors?: Record<string, string[]> } } }
    const imageErrors = err.response?.data?.errors?.['image']
    fieldError.value = imageErrors?.[0] ?? t('events.media.signature.error_upload')
  } finally {
    isUploading.value = false
  }
}

async function handleDelete(mediaId: number): Promise<void> {
  await deleteImage(mediaId)
}

const uploadProgressValues = computed(() => Object.values(uploadProgress.value))
const currentUploadPct = computed(() =>
  uploadProgressValues.value.length > 0 ? uploadProgressValues.value[0] : 0,
)
</script>

<template>
  <div class="bg-slate-800/50 border border-slate-700 rounded-2xl p-6 sm:p-8">
    <div class="mb-6">
      <h2 class="text-lg font-semibold text-white">
        {{ t('events.media.signature.heading') }}
      </h2>
      <p class="text-sm text-slate-400 mt-1">
        {{ t('events.media.signature.description') }}
      </p>
    </div>

    <!-- Loading -->
    <div v-if="loading && images.length === 0" class="flex items-center justify-center py-12">
      <div class="h-6 w-6 animate-spin rounded-full border-2 border-slate-600 border-t-emerald-400" />
    </div>

    <template v-else>
      <!-- Image grid -->
      <div
        v-if="images.length > 0"
        class="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4"
      >
        <div
          v-for="img in images"
          :key="img.id"
          class="relative group rounded-xl overflow-hidden border border-slate-600 aspect-video bg-slate-700"
        >
          <!-- Processing overlay -->
          <template v-if="img.status === 'pending' || img.status === 'processing'">
            <div class="absolute inset-0 flex items-center justify-center bg-slate-800/80">
              <div class="h-5 w-5 animate-spin rounded-full border-2 border-slate-400 border-t-white" />
            </div>
          </template>

          <!-- Failed -->
          <template v-else-if="img.status === 'failed'">
            <div class="absolute inset-0 flex items-center justify-center bg-red-900/40">
              <span class="text-xs text-red-300 text-center px-2">{{ t('events.media.signature.status_failed') }}</span>
            </div>
          </template>

          <!-- Ready image -->
          <template v-else-if="img.status === 'ready'">
            <picture>
              <source
                v-if="img.urls.thumb?.avif"
                type="image/avif"
                :srcset="img.urls.thumb.avif"
              />
              <source
                v-if="img.urls.thumb?.webp"
                type="image/webp"
                :srcset="img.urls.thumb.webp"
              />
              <img
                :src="img.urls.thumb?.jpeg"
                class="w-full h-full object-cover"
                alt=""
              />
            </picture>
          </template>

          <!-- Delete button -->
          <button
            type="button"
            class="absolute top-1.5 right-1.5 p-1 rounded-lg bg-slate-900/70 text-slate-300 opacity-0 group-hover:opacity-100 hover:text-white hover:bg-red-700/80 transition-all"
            :title="t('events.media.signature.btn_remove')"
            @click="handleDelete(img.id)"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      <!-- Upload progress -->
      <div v-if="isUploading" class="mb-4">
        <div class="h-2 bg-slate-700 rounded-full overflow-hidden">
          <div
            class="h-full bg-emerald-500 rounded-full transition-all duration-300"
            :style="{ width: `${currentUploadPct}%` }"
          />
        </div>
        <p class="text-xs text-slate-400 mt-1">{{ currentUploadPct }}%</p>
      </div>

      <!-- Dropzone (shown when under the limit) -->
      <div v-if="canUploadMore">
        <div
          class="border-2 border-dashed rounded-xl p-8 flex flex-col items-center gap-3 transition-colors cursor-pointer"
          :class="isDragging ? 'border-emerald-400 bg-emerald-900/10' : 'border-slate-600 hover:border-slate-500'"
          @dragover.prevent="onDragOver"
          @dragleave="onDragLeave"
          @drop.prevent="onDrop"
          @click="openFilePicker"
        >
          <svg class="w-8 h-8 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="1.5"
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          <span class="text-sm text-slate-300 text-center">
            {{ t('events.media.signature.dropzone_label') }}
          </span>
          <span class="text-xs text-slate-500">
            {{ t('events.media.signature.dropzone_hint', { current: images.length, max: MAX_SIGNATURE_IMAGES }) }}
          </span>
          <input
            ref="inputRef"
            type="file"
            accept="image/jpeg,image/png,image/webp"
            class="sr-only"
            @change="onFileChange"
          />
        </div>
      </div>

      <!-- At-limit message -->
      <p v-else class="text-sm text-slate-400 text-center py-4">
        {{ t('events.media.signature.limit_reached', { max: MAX_SIGNATURE_IMAGES }) }}
      </p>

      <!-- Error -->
      <p v-if="clientError || fieldError" class="mt-3 text-sm text-red-400">
        {{ clientError ?? fieldError }}
      </p>
    </template>
  </div>
</template>
